
import {
  formatVideoTimeLabel,
  formatVideoTimePrecise,
  getSegmentsTotalDuration,
  trimVideoSegments,
  VideoTrimSegment,
} from '@/utilities/ffmpeg';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

type TimelinePiece = {
  id: string;
  start: number;
  end: number;
  kept: boolean;
};

type EditorSnapshot = {
  pieces: TimelinePiece[];
  selectedPieceId: string | null;
  playheadTime: number;
};

const MIN_CLIP_SECONDS = 0.25;
const MAX_UNDO_STACK = 50;

let pieceIdCounter = 0;

function nextPieceId(): string {
  pieceIdCounter += 1;
  return `clip-${pieceIdCounter}`;
}

@Component({
  name: 'video-trim-modal',
})
export default class VideoTrimModal extends Vue {
  @Prop({ required: true, type: Boolean })
  readonly show!: boolean;

  @Prop({ required: false, default: null, validator: (value: unknown) => value == null || value instanceof File })
  readonly file!: File | null;

  /** Duration from the dropzone preview — used when modal opens before metadata reloads. */
  @Prop({ required: false, type: Number, default: null })
  readonly initialVideoDuration!: number | null;

  @Prop({ required: false, type: Number, default: null })
  readonly maxSegmentDurationSeconds!: number | null;

  timelinePieces: TimelinePiece[] = [];
  selectedPieceId: string | null = null;
  undoStack: EditorSnapshot[] = [];
  videoDuration = 0;
  /** Fixed position used for Cut — set by clicking the timeline. */
  playheadTime = 0;
  /** Temporary preview position while hovering the timeline. */
  hoverPreviewTime: number | null = null;
  isTimelineHovered = false;
  /** Prevents re-entrant seeks while skipping removed clips during playback. */
  isSkippingRemovedClip = false;
  previewUrl = '';
  isProcessing = false;
  processingMessage = '';
  errorMessage = '';

  readonly formatVideoTimeLabel = formatVideoTimeLabel;
  readonly formatVideoTimePrecise = formatVideoTimePrecise;

  get selectedPiece(): TimelinePiece | null {
    if (!this.selectedPieceId) {
      return null;
    }
    return this.timelinePieces.find((piece) => piece.id === this.selectedPieceId) || null;
  }

  get keptPieces(): TimelinePiece[] {
    return this.timelinePieces.filter((piece) => piece.kept);
  }

  /** True when at least one timeline segment is marked as removed. */
  get hasRemovedClips(): boolean {
    return this.timelinePieces.some((piece) => !piece.kept);
  }

  get internalCutTimes(): number[] {
    if (this.timelinePieces.length <= 1) {
      return [];
    }
    return this.timelinePieces.slice(1).map((piece) => piece.start);
  }

  get playheadPercent(): number {
    if (this.videoDuration <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (this.playheadTime / this.videoDuration) * 100));
  }

  get hoverPreviewPercent(): number {
    if (this.videoDuration <= 0 || this.hoverPreviewTime == null) {
      return 0;
    }
    return Math.min(100, Math.max(0, (this.hoverPreviewTime / this.videoDuration) * 100));
  }

  /** Blue preview line — hidden when hover matches the fixed red playhead. */
  get showHoverPreviewPlayhead(): boolean {
    if (!this.isTimelineHovered || this.hoverPreviewTime == null) {
      return false;
    }
    return Math.abs(this.hoverPreviewTime - this.playheadTime) > 0.05;
  }

  get canCutAtPlayhead(): boolean {
    return this.canCutAt(this.playheadTime);
  }

  get outputDuration(): number {
    return getSegmentsTotalDuration(this.keptPieces);
  }

  get outputDurationWarning(): boolean {
    return (
      !!this.maxSegmentDurationSeconds &&
      this.outputDuration > this.maxSegmentDurationSeconds + 0.05
    );
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canApply(): boolean {
    return (
      !!this.file &&
      this.videoDuration > 0 &&
      !this.isProcessing &&
      !this.outputDurationWarning &&
      this.keptPieces.length > 0 &&
      this.outputDuration > 0.05
    );
  }

  @Watch('show')
  onShowChanged(isOpen: boolean) {
    if (isOpen) {
      this.resetState();
      this.refreshPreviewUrl();
      if (this.initialVideoDuration && this.initialVideoDuration > 0) {
        this.applyVideoDuration(this.initialVideoDuration);
      }
      this.$nextTick(() => this.syncVideoDurationFromElement());
      document.addEventListener('keydown', this.onKeyDown);
    } else {
      this.revokePreviewUrl();
      document.removeEventListener('keydown', this.onKeyDown);
    }
  }

  @Watch('file')
  onFileChanged() {
    if (this.show) {
      this.resetState();
      this.refreshPreviewUrl();
    }
  }

  beforeDestroy() {
    this.revokePreviewUrl();
    document.removeEventListener('keydown', this.onKeyDown);
  }

  resetState() {
    this.timelinePieces = [];
    this.selectedPieceId = null;
    this.undoStack = [];
    this.videoDuration = 0;
    this.playheadTime = 0;
    this.hoverPreviewTime = null;
    this.isTimelineHovered = false;
    this.isProcessing = false;
    this.processingMessage = '';
    this.errorMessage = '';
  }

  refreshPreviewUrl() {
    this.revokePreviewUrl();
    if (this.file) {
      this.previewUrl = URL.createObjectURL(this.file);
      this.$nextTick(() => this.syncVideoDurationFromElement());
    }
  }

  revokePreviewUrl() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = '';
    }
  }

  pushUndoSnapshot() {
    this.undoStack.push({
      pieces: this.timelinePieces.map((piece) => ({ ...piece })),
      selectedPieceId: this.selectedPieceId,
      playheadTime: this.playheadTime,
    });
    if (this.undoStack.length > MAX_UNDO_STACK) {
      this.undoStack.shift();
    }
  }

  undo() {
    const snapshot = this.undoStack.pop();
    if (!snapshot) {
      return;
    }
    this.timelinePieces = snapshot.pieces.map((piece) => ({ ...piece }));
    this.selectedPieceId = snapshot.selectedPieceId;
    this.playheadTime = snapshot.playheadTime;
    this.hoverPreviewTime = null;
    this.isTimelineHovered = false;
    this.seekVideoTo(this.playheadTime);
  }

  onVideoMetadataLoaded() {
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (!video || !Number.isFinite(video.duration)) {
      return;
    }
    this.applyVideoDuration(video.duration);
  }

  /** Fallback when metadata is already available before the loadedmetadata event fires. */
  syncVideoDurationFromElement() {
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (!video || video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }
    this.applyVideoDuration(video.duration);
  }

  applyVideoDuration(duration: number) {
    if (duration <= 0) {
      return;
    }
    const alreadyInitialized =
      this.videoDuration > 0 &&
      Math.abs(this.videoDuration - duration) < 0.01 &&
      this.timelinePieces.length > 0;
    if (alreadyInitialized) {
      return;
    }
    this.videoDuration = duration;
    this.timelinePieces = [{ id: nextPieceId(), start: 0, end: duration, kept: true }];
    this.selectedPieceId = null;
    this.undoStack = [];
    this.playheadTime = 0;
    this.seekVideoTo(0);
  }

  /** Sync red playhead with playback; skip removed clips while playing. */
  onPreviewTimeUpdate() {
    if (this.isTimelineHovered || this.isSkippingRemovedClip) {
      return;
    }
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (!video || video.paused) {
      return;
    }
    this.playheadTime = video.currentTime;
    if (this.hasRemovedClips) {
      this.skipRemovedClipDuringPlayback(video);
    }
  }

  /** When playback starts inside a removed clip, jump to the next kept segment. */
  onVideoPlay() {
    if (this.isTimelineHovered || !this.hasRemovedClips) {
      return;
    }
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (!video) {
      return;
    }
    const piece = this.findPieceAtTime(video.currentTime);
    if (piece && !piece.kept) {
      this.jumpToNextKeptSegment(video, video.currentTime);
    }
  }

  /** Sync playhead when scrubbing with native video controls while paused. */
  onVideoSeeked() {
    if (this.isTimelineHovered || this.isSkippingRemovedClip) {
      return;
    }
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (video && video.paused) {
      this.playheadTime = video.currentTime;
    }
  }

  /** Returns the timeline piece containing the given source time, if any. */
  findPieceAtTime(time: number): TimelinePiece | null {
    for (let index = 0; index < this.timelinePieces.length; index += 1) {
      const piece = this.timelinePieces[index];
      const isLast = index === this.timelinePieces.length - 1;
      if (time >= piece.start && (time < piece.end || (isLast && time <= piece.end))) {
        return piece;
      }
    }
    return null;
  }

  /** Finds the next kept segment after the given source time (or after the current clip end). */
  findNextKeptPieceAfter(time: number): TimelinePiece | null {
    const currentPiece = this.findPieceAtTime(time);
    const searchFrom = currentPiece ? currentPiece.end : time;
    return this.timelinePieces.find((piece) => piece.kept && piece.start >= searchFrom - 0.01) || null;
  }

  /** During playback, skip over removed segments so preview matches the export. */
  skipRemovedClipDuringPlayback(video: HTMLVideoElement) {
    const piece = this.findPieceAtTime(video.currentTime);
    if (!piece || piece.kept) {
      return;
    }
    this.jumpToNextKeptSegment(video, video.currentTime);
  }

  /** Seeks playback to the next kept clip, or stops at the end of the last kept clip. */
  jumpToNextKeptSegment(video: HTMLVideoElement, fromTime: number) {
    const nextKept = this.findNextKeptPieceAfter(fromTime);
    this.isSkippingRemovedClip = true;
    try {
      if (nextKept) {
        video.currentTime = nextKept.start;
        this.playheadTime = nextKept.start;
        return;
      }
      const lastKept = this.keptPieces[this.keptPieces.length - 1];
      if (lastKept) {
        video.currentTime = lastKept.end;
        this.playheadTime = lastKept.end;
      }
      video.pause();
    } finally {
      this.isSkippingRemovedClip = false;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.show || this.isProcessing) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !this.isTypingInInput(event)) {
      event.preventDefault();
      this.undo();
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.removeSelectedClip();
    }
    if (event.key === 'Escape') {
      this.selectedPieceId = null;
    }
  }

  isTypingInInput(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('input, textarea, select, [contenteditable]');
  }

  timeFromMouseEvent(event: MouseEvent): number {
    const track = this.$refs.timelineTrack as HTMLElement | undefined;
    if (!track || this.videoDuration <= 0) {
      return 0;
    }
    const rect = track.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(this.videoDuration, ratio * this.videoDuration));
  }

  canCutAt(time: number): boolean {
    return this.timelinePieces.some(
      (item) => time >= item.start + MIN_CLIP_SECONDS && time <= item.end - MIN_CLIP_SECONDS,
    );
  }

  /** Updates video element only — does not change the fixed playhead. */
  seekVideoTo(time: number) {
    const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
    if (video && Math.abs(video.currentTime - time) > 0.05) {
      video.currentTime = time;
    }
  }

  /** Locks the red playhead and position label at the given time. */
  fixPlayheadPosition(time: number) {
    this.playheadTime = time;
    this.hoverPreviewTime = time;
    this.seekVideoTo(time);
  }

  onTimelineMouseEnter() {
    this.isTimelineHovered = true;
  }

  /** Preview position on hover without fixing the playhead. */
  onTimelineMouseMove(event: MouseEvent) {
    const time = this.timeFromMouseEvent(event);
    this.hoverPreviewTime = time;
    this.seekVideoTo(time);
  }

  /** Restore video to the fixed playhead when leaving the timeline. */
  onTimelineMouseLeave() {
    this.isTimelineHovered = false;
    this.hoverPreviewTime = null;
    this.seekVideoTo(this.playheadTime);
  }

  /** Click on the timeline track fixes the red playhead immediately. */
  onTimelineMouseDown(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    this.fixPlayheadPosition(this.timeFromMouseEvent(event));
  }

  onPieceMouseDown(pieceId: string, event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    this.fixPlayheadPosition(this.timeFromMouseEvent(event));
    this.selectedPieceId = pieceId;
  }

  pieceBarStyle(piece: TimelinePiece): Record<string, string> {
    const left = (piece.start / this.videoDuration) * 100;
    const width = ((piece.end - piece.start) / this.videoDuration) * 100;
    return {
      left: `${left}%`,
      width: `${Math.max(width, 0.15)}%`,
    };
  }

  pieceVisualClass(piece: TimelinePiece): string {
    const selected = piece.id === this.selectedPieceId;
    if (!piece.kept) {
      return selected
        ? 'bg-gray-400/80 video-clip-removed ring-2 ring-gray-600 ring-inset cursor-pointer'
        : 'bg-gray-400/60 video-clip-removed cursor-pointer';
    }
    return selected
      ? 'bg-primary-500 ring-2 ring-primary-800 ring-inset z-10 cursor-pointer'
      : 'bg-primary-400 hover:bg-primary-500 z-0 cursor-pointer';
  }

  pieceTooltip(piece: TimelinePiece): string {
    const label = `${formatVideoTimeLabel(piece.start)} – ${formatVideoTimeLabel(piece.end)}`;
    return piece.kept ? `Keep: ${label}` : `Removed: ${label}`;
  }

  addCut(time: number) {
    const piece = this.timelinePieces.find(
      (item) => time >= item.start + MIN_CLIP_SECONDS && time <= item.end - MIN_CLIP_SECONDS,
    );
    if (!piece) {
      return false;
    }

    const secondPiece: TimelinePiece = {
      id: nextPieceId(),
      start: time,
      end: piece.end,
      kept: piece.kept,
    };
    piece.end = time;

    const index = this.timelinePieces.findIndex((item) => item.id === piece.id);
    this.timelinePieces = [
      ...this.timelinePieces.slice(0, index + 1),
      secondPiece,
      ...this.timelinePieces.slice(index + 1),
    ];
    return true;
  }

  cutAtPlayhead() {
    if (!this.canCutAtPlayhead) {
      return;
    }
    this.pushUndoSnapshot();
    this.addCut(this.playheadTime);
  }

  removeSelectedClip() {
    const piece = this.selectedPiece;
    if (!piece || !piece.kept) {
      return;
    }
    this.pushUndoSnapshot();
    piece.kept = false;
    this.timelinePieces = [...this.timelinePieces];
  }

  restoreSelectedClip() {
    const piece = this.selectedPiece;
    if (!piece || piece.kept) {
      return;
    }
    this.pushUndoSnapshot();
    piece.kept = true;
    this.timelinePieces = [...this.timelinePieces];
  }

  keepFirstSecondsOnly() {
    if (!this.maxSegmentDurationSeconds || this.videoDuration <= 0) {
      return;
    }
    this.pushUndoSnapshot();
    const limit = Math.min(this.videoDuration, this.maxSegmentDurationSeconds);
    this.addCut(limit);
    this.timelinePieces.forEach((piece) => {
      piece.kept = piece.start < limit - 0.01;
    });
    this.timelinePieces = [...this.timelinePieces];
    this.selectedPieceId = null;
    this.playheadTime = Math.min(this.playheadTime, limit);
    this.seekVideoTo(this.playheadTime);
  }

  onUpdateShow(value: boolean) {
    if (this.isProcessing) {
      return;
    }
    this.$emit('update:show', value);
  }

  close() {
    if (this.isProcessing) {
      return;
    }
    this.$emit('update:show', false);
  }

  getSegmentsForExport(): VideoTrimSegment[] {
    return this.keptPieces.map(({ start, end }) => ({ start, end }));
  }

  async applyTrim() {
    if (!this.canApply || !this.file) {
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';
    this.processingMessage = 'Loading video engine…';

    try {
      const segments = this.getSegmentsForExport();
      const trimmed = await trimVideoSegments(
        this.file,
        segments,
        () => {
          if (this.processingMessage === 'Loading video engine…') {
            this.processingMessage = 'Processing video…';
          }
        },
        (message) => {
          this.processingMessage = message;
        },
      );
      this.$emit('trim', trimmed);
      this.$emit('update:show', false);
    } catch (error) {
      this.errorMessage =
        error instanceof Error
          ? error.message
          : 'Could not process the video. Try fewer or shorter clips.';
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
    }
  }
}
